// Lib
import React, { ReactElement, useLayoutEffect, useRef } from 'react';

// Utils
import platformSingleton from '../../../../../../../platform/platformSingleton';

// Components
import Icon from '../../../../../../../components/icons/Icon';
import Button from '../../../../../../../components/buttons/Button';

// Types
import { PromptShortcut } from '../../../../../../../../common/ai/aiPromptShortcutTypes';

// Styles
import './AiAssistantPromptInput.scss';
import '../../../../../../../components/popupPanel/types/PopupTextInput.scss';

interface AiAssistantPromptInputProps {
    shouldDelayFocus: boolean;
    promptText: string;
    promptShortcut?: PromptShortcut;
    setPromptText: (promptText: string) => void;
    submitForm: (event?: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>) => Promise<void>;
}

const AiAssistantPromptInput = (props: AiAssistantPromptInputProps): ReactElement => {
    const { promptText, setPromptText, submitForm, promptShortcut, shouldDelayFocus } = props;

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const focusInput = () => {
        // if this is a touch device, don't auto-focus the input — otherwise virtual keyboard will hide the popup
        if (platformSingleton.features.isTouch) return;

        inputRef.current?.focus();
    };

    useLayoutEffect(() => {
        if (!inputRef.current) return;

        inputRef.current.addEventListener('paste', (event) => event.stopPropagation());

        // We don't want to delay focus when the popup is mounted otherwise there will be a noticeable delay
        // between when the input shows and the cursor enters.
        // But we need to delay focus other times to ensure that the CSS transition plays correctly
        if (!shouldDelayFocus) {
            focusInput();
            return;
        }

        // Wait for any CSS transitions to complete before applying focus
        setTimeout(() => {
            if (!inputRef.current) return;
            focusInput();
        }, 200);
    }, []);

    const promptTextSubmitDisabled = promptText.length === 0;

    return (
        <div className="AiAssistantPromptInput">
            <textarea
                ref={inputRef}
                value={promptText}
                onChange={(event) => {
                    setPromptText(event.target.value);
                }}
                onKeyDown={(event) => {
                    // submit on enter if shift is not pressed
                    if (event.key !== 'Enter' || event.shiftKey) return;

                    // if submit is disabled, don't handle this event
                    if (promptTextSubmitDisabled) {
                        event.preventDefault();
                        return;
                    }

                    // otherwise, submit the form
                    submitForm(event);
                }}
                className="PopupTextInput"
                placeholder={promptShortcut?.placeholderText || 'Write a sentence or two describing what you’d like'}
            />

            <Button type="submit" className="submit-button" disabled={promptTextSubmitDisabled}>
                <Icon name="ai-submit" />
                Submit
            </Button>
        </div>
    );
};

export default AiAssistantPromptInput;
